import {
  Icon,
  CheckCircleFilledSVG,
  StopSVG,
  Anchor,
  Button,
} from "@newsela/angelou";
import { withTrackEvent, objectTypes, actionNames } from "@newsela/snitch";

import { CardResource } from "@/components/ImplementationGuide/types/CardResource.interface";
import ResourceCard from "../ResourceCard";
import {
  cardsCss,
  sectionCss,
  titleCss,
  completedCss,
  notStartedCss,
  sectionContentCss,
  titleContainerCss,
  subtitleCss,
  imageCss,
  imageContainerCss,
  cardsForPDFOutputCss,
} from "./FormSection.style";
import ToDoSection from "./ToDoSection";

type AnchorProps = React.ComponentProps<typeof Anchor>;
const AnchorWithTrackEvent = withTrackEvent(
  Anchor,
) as React.ForwardRefExoticComponent<
  AnchorProps & React.RefAttributes<HTMLAnchorElement>
>;

interface FormSectionProps {
  /**
   * Text for the section title H2 element
   */
  title: string;

  /**
   * Text for the section subtitle element
   */
  subtitle: string;

  /**
   * Image for the section
   */
  image?: string | undefined;

  /**
   * Boolean that indicates if the form is completed or not
   */
  isCompleted: boolean;

  /**
   * List of resources to display in the section
   */
  resources: CardResource[];

  /**
   * The number of questions that don't have responses.
   */
  numberOfIncompleteQuestions: number;

  /**
   * Is the FormSection being rendered for PDF output
   */
  isPDFOutput?: boolean;

  /**
   * The href of the form's edit button
   */
  editButtonUrl: string;
}

const FormSection = (props: FormSectionProps) => {
  const {
    title,
    subtitle,
    image,
    isCompleted,
    resources,
    numberOfIncompleteQuestions,
    isPDFOutput,
    editButtonUrl,
  } = props;
  const todoItems = resources
    .map((resource) => resource.todo_item)
    .filter((item) => item !== "");
  const eventPropertiesForEditButton = {
    actionPrefix: "Initiate",
    objectType: objectTypes.fullPage,
    actionName: actionNames.update,
    streamProperties: {
      description: `Edit the Admin quick start's ${title} form`,
    },
  };

  return (
    <div css={sectionCss}>
      <div css={sectionContentCss}>
        <div>
          <div css={titleContainerCss}>
            <h2 css={titleCss}>{title}</h2>
            <div css={subtitleCss}>{subtitle}</div>
            {isCompleted ? (
              <div css={completedCss}>
                <Icon SvgComponent={CheckCircleFilledSVG} size="MD" />
                <div>Ready for implementation</div>
              </div>
            ) : title && !isCompleted ? (
              <div css={notStartedCss}>
                <Icon SvgComponent={StopSVG} size="MD" />
                <div>Pending more information</div>
              </div>
            ) : null}
          </div>
          <ToDoSection
            options={todoItems}
            useBulletPoints
            isCompleted={isCompleted}
            numberOfIncompleteQuestions={numberOfIncompleteQuestions}
          />
        </div>
        <div css={isPDFOutput && imageContainerCss}>
          {image && <img src={image} alt={title} css={imageCss} />}
        </div>
      </div>
      <div css={[cardsCss, isPDFOutput && cardsForPDFOutputCss]}>
        {resources.map((card) => (
          <ResourceCard
            title={card.resource_name}
            anchorLabel={card.resource_link_text}
            subtitle={card.resource_subtitle}
            href={card.resource_link}
            key={card.resource_link}
            alternativeHref={card.alternative_link}
            alternativeHrefLabel={card.alternative_link_text}
            isPDFOutput={isPDFOutput}
          />
        ))}
      </div>
      {!isPDFOutput && (
        <AnchorWithTrackEvent
          dataProps={{
            "data-qa-selector": `edit-the-${title}-implementation-guide-form`,
          }}
          styleAnchorAsButton={{
            flavor: isCompleted
              ? Button.flavors.secondary
              : Button.flavors.primary,
            size: Button.sizes.lg,
          }}
          href={editButtonUrl}
          {...eventPropertiesForEditButton}
        >
          {isCompleted ? "Edit" : "Complete"} {title.toLowerCase()} section
        </AnchorWithTrackEvent>
      )}
    </div>
  );
};

export default FormSection;
